import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeesApi, timeEntriesApi, schedulesApi, messagesApi, timeOffApi } from '../services/api';
import { secureStorage } from '../utils/secureStorage';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Calendar as ShadCalendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Calendar,
  Clock,
  MessageSquare,
  ArrowLeftRight,
  User,
  LogIn,
  LogOut,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Building2,
  ChevronLeft,
  ChevronRight,
  Send,
  CheckCheck,
  Settings,
  CalendarDays,
  Ban,
  StickyNote,
} from 'lucide-react';
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays } from 'date-fns';
import { formatTime as portalFormatTime } from '../utils/timeFormat';
import AvailabilityEditor from '../components/AvailabilityEditor';
import { availabilityApi, shiftRequestsApi, scheduleNotesApi } from '../services/api';
import MessageAttachments from '../components/MessageAttachments';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_showtime-staff/artifacts/1ijkahsm_logo%20jf.png";

// Stable references for shadcn Calendar — avoid re-creating these objects on every render.
const BLACKOUT_MODIFIER_CLASSES = { blackout: 'bg-red-100 text-red-400 line-through' };

const EmployeePortal = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('login'); // login, dashboard
  const [employeeId, setEmployeeId] = useState('');
  const [passcode, setPasscode] = useState('');
  const [showPasscode, setShowPasscode] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true to check session
  const [error, setError] = useState('');
  
  // Data
  const [schedules, setSchedules] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [messages, setMessages] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [openShifts, setOpenShifts] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [activeTab, setActiveTab] = useState('schedule');
  const [availability, setAvailability] = useState(null);
  const [shiftRequestModal, setShiftRequestModal] = useState(null);
  const [myShiftRequests, setMyShiftRequests] = useState([]);
  const [shiftRequestForm, setShiftRequestForm] = useState({ type: 'cover', target_schedule_id: '', target_employee_id: '', reason: '' });
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [replyTexts, setReplyTexts] = useState({}); // For reply inputs per message
  const [recipients, setRecipients] = useState([]);
  const [newMessage, setNewMessage] = useState({
    subject: '',
    content: '',
    recipient_id: '',
    recipient_name: '',
  });
  const [newTimeOff, setNewTimeOff] = useState({
    start_date: '',
    end_date: '',
    type: 'vacation',
    reason: '',
  });
  const [blackouts, setBlackouts] = useState([]);
  const [blackoutHit, setBlackoutHit] = useState(null);
  const [scheduleNotes, setScheduleNotes] = useState([]);

  const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  // Memoized derivations to avoid recomputing on every render
  const totalHoursThisWeek = useMemo(
    () => timeEntries.reduce((sum, e) => sum + (e.hours_worked || 0), 0),
    [timeEntries]
  );

  const schedulesByDate = useMemo(() => {
    const map = {};
    schedules.forEach((s) => {
      (map[s.date] = map[s.date] || []).push(s);
    });
    return map;
  }, [schedules]);

  const openShiftsByDate = useMemo(() => {
    const map = {};
    openShifts.forEach((s) => {
      (map[s.date] = map[s.date] || []).push(s);
    });
    return map;
  }, [openShifts]);

  const unreadMessageCount = useMemo(
    () => messages.filter((m) => !m.read_by?.includes(employee?.id)).length,
    [messages, employee?.id]
  );

  const groupRecipients = useMemo(
    () => recipients.filter((r) => r.type === 'location_group' || r.type === 'all_locations'),
    [recipients]
  );

  const individualRecipients = useMemo(
    () => recipients.filter((r) => r.type === 'individual'),
    [recipients]
  );

  // Expand all blackouts into a Set of date strings ("YYYY-MM-DD") for fast lookup
  const blackoutDateSet = useMemo(() => {
    const set = new Set();
    blackouts.forEach((b) => {
      if (!b.start_date) return;
      const start = new Date(`${b.start_date}T00:00:00`);
      const end = new Date(`${b.end_date || b.start_date}T00:00:00`);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        set.add(format(d, 'yyyy-MM-dd'));
      }
    });
    return set;
  }, [blackouts]);

  const upcomingBlackouts = useMemo(() => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    return blackouts
      .filter((b) => (b.end_date || b.start_date) >= todayStr)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .slice(0, 6);
  }, [blackouts]);

  const isDateInBlackout = useMemo(() => {
    return (yyyymmdd) => blackoutDateSet.has(yyyymmdd);
  }, [blackoutDateSet]);

  const blackoutModifiers = useMemo(
    () => ({ blackout: (d) => isDateInBlackout(format(d, 'yyyy-MM-dd')) }),
    [isDateInBlackout]
  );

  const scheduleNotesByDate = useMemo(() => {
    const map = {};
    scheduleNotes.forEach((n) => { if (n.date) map[n.date] = n; });
    return map;
  }, [scheduleNotes]);

  const checkRangeForBlackout = (startStr, endStr) => {
    if (!startStr || !endStr) return null;
    const start = new Date(`${startStr}T00:00:00`);
    const end = new Date(`${endStr}T00:00:00`);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = format(d, 'yyyy-MM-dd');
      if (blackoutDateSet.has(key)) {
        const hit = blackouts.find(
          (b) => key >= b.start_date && key <= (b.end_date || b.start_date)
        );
        return { date: key, label: hit?.label || 'Blackout', block: hit?.block !== false };
      }
    }
    return null;
  };

  // Restore session on page load/refresh
  useEffect(() => {
    const restoreSession = async () => {
      const token = secureStorage.getItem('employee_portal_token');
      const storedEmployee = secureStorage.getItem('employee_portal_employee');
      
      if (token && storedEmployee) {
        try {
          const emp = storedEmployee;
          setEmployee(emp);
          setStep('dashboard');
          await loadData(emp);
        } catch (err) {
          // Invalid stored data, clear it
          secureStorage.removeItem('employee_portal_token');
          secureStorage.removeItem('employee_portal_employee');
          console.error('Session restore failed:', err);
        }
      }
      setLoading(false);
    };
    
    restoreSession();
  }, []);

  const handleLogin = async () => {
    if (!employeeId || !passcode) {
      setError('Please enter your Employee ID and passcode');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Verify passcode and get token
      const authResult = await employeesApi.verifyPasscode(employeeId.toUpperCase(), passcode);
      
      // Store token for authenticated API calls
      if (authResult.access_token) {
        secureStorage.setItem('employee_portal_token', authResult.access_token);
      }
      
      // Use employee data from auth result or fetch separately
      const emp = authResult.employee || await employeesApi.getByNumber(employeeId.toUpperCase());
      
      // Store employee data for session persistence
      secureStorage.setItem('employee_portal_employee', emp);
      
      setEmployee(emp);
      setStep('dashboard');
      
      // Load data
      loadData(emp);
    } catch (err) {
      setError(err.message || 'Invalid Employee ID or passcode');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (emp) => {
    if (!emp) return;
    
    try {
      const startDate = format(weekStart, 'yyyy-MM-dd');
      const endDate = format(weekEnd, 'yyyy-MM-dd');
      
      // Load schedules for this employee
      const scheduleData = await schedulesApi.getAll({
        locationId: emp.location_id,
        startDate,
        endDate,
      });
      setSchedules(scheduleData.filter(s => s.employee_id === emp.id));
      
      // Load time entries
      const entriesData = await timeEntriesApi.getAll({
        employeeId: emp.id,
        startDate,
        endDate,
      });
      setTimeEntries(entriesData);
      
      // Load messages for location
      const messagesData = await messagesApi.getAll({
        locationId: emp.location_id,
      });
      setMessages(messagesData);
      
      // Load open shifts for this location
      try {
        const openShiftsData = await schedulesApi.getOpenShifts(emp.location_id, startDate, endDate);
        setOpenShifts(openShiftsData || []);
      } catch (e) {
        console.warn('Could not load open shifts:', e);
        setOpenShifts([]);
      }
      
      // Load recipients for messaging
      try {
        const token = secureStorage.getItem('employee_portal_token');
        if (token) {
          const recipientsData = await messagesApi.getRecipients();
          setRecipients(recipientsData.recipients || []);
        }
      } catch (e) {
        console.warn('Could not load recipients:', e);
      }
      
      // Load time-off requests for this employee
      try {
        const timeOffData = await timeOffApi.getByEmployee(emp.id);
        setTimeOffRequests(timeOffData || []);
      } catch (e) {
        setTimeOffRequests([]);
      }

      // Load blackout dates for this employee's location so we can disable them on the calendar
      try {
        const bo = await timeOffApi.listBlackouts(emp.location_id);
        setBlackouts(bo || []);
      } catch (e) {
        console.warn('Could not load blackouts:', e);
        setBlackouts([]);
      }

      // Load daily schedule notes for this employee's location/week
      try {
        const notes = await scheduleNotesApi.list({
          locationId: emp.location_id,
          startDate,
          endDate,
        });
        setScheduleNotes(notes || []);
      } catch (e) {
        setScheduleNotes([]);
      }

      // Load availability
      try {
        const avail = await availabilityApi.getMine();
        setAvailability(avail);
      } catch (e) {
        console.warn('Could not load availability:', e);
      }

      // Load my shift requests
      try {
        const sr = await shiftRequestsApi.list({ employeeId: emp.id });
        setMyShiftRequests(sr || []);
      } catch (e) {
        setMyShiftRequests([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const handleClaimShift = async (scheduleId) => {
    setLoading(true);
    try {
      await schedulesApi.claimShift(scheduleId);
      alert('Shift claim submitted! Waiting for manager approval.');
      await loadData(employee);
    } catch (err) {
      setError(err.message || 'Failed to claim shift');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (parentMessage) => {
    const replyText = replyTexts[parentMessage.id];
    if (!replyText?.trim()) return;
    
    setLoading(true);
    try {
      await messagesApi.create({
        location_id: employee.location_id,
        type: 'message',
        subject: `Re: ${parentMessage.subject || 'Message'}`,
        content: replyText,
        is_urgent: false,
        reply_to: parentMessage.id,
        recipient_type: 'individual',
        recipient_id: parentMessage.sender_id,
        recipient_name: parentMessage.sender_name,
      });
      
      // Clear reply text and reload messages
      setReplyTexts(prev => ({...prev, [parentMessage.id]: ''}));
      await loadData(employee);
    } catch (err) {
      setError(err.message || 'Failed to send reply');
    } finally {
      setLoading(false);
    }
  };

  // Helper: make API calls using the employee portal token specifically
  const employeeApiFetch = async (endpoint, options = {}) => {
    const empToken = secureStorage.getItem('employee_portal_token');
    if (!empToken) return null;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const response = await fetch(`${API_URL}/api${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${empToken}`,
        ...options.headers,
      },
    });
    if (!response.ok) return null;
    return response.json();
  };

  const handleClearMessageNotifications = async () => {
    try {
      await employeeApiFetch('/messages/mark-all-read', { method: 'POST' });
      // Reload messages to update read_by status
      const messagesData = await messagesApi.getAll({ locationId: employee.location_id });
      setMessages(messagesData);
    } catch (err) {
      console.error('Failed to clear notifications:', err);
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    try {
      await employeeApiFetch(`/messages/${messageId}/read`, { method: 'POST' });
      setMessages(prev => prev.map(m =>
        m.id === messageId
          ? { ...m, read_by: [...(m.read_by || []), employee?.id] }
          : m
      ));
    } catch (err) {
      console.error('Failed to mark message as read:', err);
    }
  };


  const handleSendNewMessage = async () => {
    if (!newMessage.content?.trim() || !newMessage.recipient_id) {
      setError('Please select a recipient and enter a message');
      return;
    }
    
    setLoading(true);
    try {
      const recipient = recipients.find(r => r.id === newMessage.recipient_id);
      await messagesApi.create({
        location_id: employee.location_id,
        type: 'message',
        subject: newMessage.subject || 'Message',
        content: newMessage.content,
        is_urgent: false,
        recipient_type: recipient?.type || 'individual',
        recipient_id: newMessage.recipient_id,
        recipient_name: recipient?.name || newMessage.recipient_name,
      });
      
      setShowNewMessageModal(false);
      setNewMessage({ subject: '', content: '', recipient_id: '', recipient_name: '' });
      await loadData(employee);
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitTimeOff = async () => {
    if (!newTimeOff.start_date || !newTimeOff.end_date) {
      setError('Please select start and end dates');
      return;
    }
    
    setLoading(true);
    try {
      await timeOffApi.create({
        employee_id: employee.id,
        employee_name: employee.name,
        location_id: employee.location_id,
        ...newTimeOff,
      });
      
      // Refresh time off requests
      const timeOffData = await timeOffApi.getByEmployee(employee.id);
      setTimeOffRequests(timeOffData || []);
      
      setShowTimeOffModal(false);
      setNewTimeOff({ start_date: '', end_date: '', type: 'vacation', reason: '' });
    } catch (err) {
      setError(err.message || 'Failed to submit time off request');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employee) {
      loadData(employee);
    }
  }, [selectedWeek]);

  const handleLogout = () => {
    secureStorage.removeItem('employee_portal_token');
    secureStorage.removeItem('employee_portal_employee');
    setEmployee(null);
    setEmployeeId('');
    setPasscode('');
    setStep('login');
    setSchedules([]);
    setTimeEntries([]);
    setMessages([]);
    setTimeOffRequests([]);
  };

  const goToKiosk = () => {
    navigate('/clock');
  };

  // Show loading while checking session
  if (loading && step === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-violet-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600 mb-4" />
            <p className="text-gray-600">Restoring session...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Login Screen
  if (step === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-violet-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <img src={LOGO_URL} alt="Logo" className="w-16 h-16 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900">Employee Portal</h1>
              <p className="text-gray-500 mt-1">View schedules, time entries & messages</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Employee ID</Label>
                <Input
                  placeholder="e.g., DT-001"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value.toUpperCase())}
                  className="h-12 font-mono"
                  data-testid="portal-employee-id"
                />
              </div>

              <div>
                <Label>Passcode</Label>
                <div className="relative">
                  <Input
                    type={showPasscode ? 'text' : 'password'}
                    placeholder="Enter your passcode"
                    value={passcode}
                    onChange={(e) => setPasscode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    className="h-12 pr-10"
                    data-testid="portal-passcode"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasscode(!showPasscode)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  >
                    {showPasscode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}

              <Button
                onClick={handleLogin}
                className="w-full h-12 bg-purple-600 hover:bg-purple-700"
                disabled={loading}
                data-testid="portal-login-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <User className="w-4 h-4 mr-2" />}
                Access Portal
              </Button>

              <div className="pt-4 border-t text-center">
                <Button variant="outline" onClick={goToKiosk} className="gap-2">
                  <Clock className="w-4 h-4" />
                  Go to Clock In/Out
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Logo" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-gray-900">Employee Portal</h1>
              <p className="text-sm text-gray-500">{employee?.location_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-medium text-gray-900">{employee?.name}</p>
              <p className="text-sm text-gray-500 font-mono">{employee?.employee_number}</p>
            </div>
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-purple-100 text-purple-600">
                {employee?.name?.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <Button variant="outline" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-0 shadow-md cursor-pointer hover:shadow-lg transition-shadow" onClick={goToKiosk}>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-purple-100 p-3 rounded-xl">
                <LogIn className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Clock In/Out</p>
                <p className="text-sm text-gray-500">Start or end your shift</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-xl">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">This Week</p>
                <p className="text-sm text-gray-500">{schedules.length} scheduled shifts</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-md">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="bg-green-100 p-3 rounded-xl">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Hours This Week</p>
                <p className="text-sm text-gray-500">
                  {totalHoursThisWeek.toFixed(1)} hours
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="schedule" className="gap-2">
              <Calendar className="w-4 h-4" />
              My Schedule
            </TabsTrigger>
            <TabsTrigger value="hours" className="gap-2">
              <Clock className="w-4 h-4" />
              My Hours
            </TabsTrigger>
            <TabsTrigger value="timeoff" className="gap-2">
              <ArrowLeftRight className="w-4 h-4" />
              Time Off
            </TabsTrigger>
            <TabsTrigger value="messages" className="gap-2">
              <MessageSquare className="w-4 h-4" />
              Messages
              {messages.filter(m => !m.read_by?.includes(employee?.id)).length > 0 && (
                <Badge className="ml-1 bg-red-500">{unreadMessageCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="employee-settings-tab">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="availability" className="gap-2" data-testid="employee-availability-tab">
              <CalendarDays className="w-4 h-4" />
              Availability
            </TabsTrigger>
          </TabsList>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>My Schedule</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[200px] text-center">
                    {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                  </span>
                  <Button variant="outline" size="icon" onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map((day) => {
                    const dateKey = format(day, 'yyyy-MM-dd');
                    const daySchedules = schedulesByDate[dateKey] || [];
                    const dayOpenShifts = openShiftsByDate[dateKey] || [];
                    const isToday = dateKey === format(new Date(), 'yyyy-MM-dd');
                    
                    return (
                      <div
                        key={day.toISOString()}
                        className={`p-3 rounded-lg border ${isToday ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}
                      >
                        <p className={`text-xs font-medium ${isToday ? 'text-purple-600' : 'text-gray-500'}`}>
                          {format(day, 'EEE')}
                        </p>
                        <p className={`text-lg font-bold ${isToday ? 'text-purple-900' : 'text-gray-900'}`}>
                          {format(day, 'd')}
                        </p>
                        {scheduleNotesByDate[dateKey] && (
                          <div className="mt-1 p-1.5 bg-yellow-50 border border-yellow-200 rounded text-[10px] text-yellow-900" data-testid={`employee-day-note-${dateKey}`}>
                            <span className="font-semibold inline-flex items-center gap-0.5"><StickyNote className="w-2.5 h-2.5" /> Notes:</span>
                            <span className="block whitespace-pre-wrap">{scheduleNotesByDate[dateKey].note}</span>
                          </div>
                        )}
                        {daySchedules.length > 0 ? (
                          daySchedules.map((s) => (
                            <div key={s.id || `${s.start_time}-${s.end_time}`} className="mt-2 p-2 bg-purple-100 rounded text-xs">
                              <p className="font-medium text-purple-900">{portalFormatTime(`2000-01-01T${s.start_time}:00`, employee?.time_format)} - {portalFormatTime(`2000-01-01T${s.end_time}:00`, employee?.time_format)}</p>
                              {s.shift_name && <p className="text-purple-600">{s.shift_name}</p>}
                              {s.station && <p className="text-purple-500 text-xs">{s.station}</p>}
                              {s.notes && <p className="text-gray-500 text-xs italic">{s.notes}</p>}
                              {s.id && (
                                <button
                                  onClick={() => setShiftRequestModal({ schedule: s, type: 'cover' })}
                                  className="mt-1 text-[10px] text-purple-700 hover:underline font-medium"
                                  data-testid={`request-cover-${s.id}`}
                                >
                                  Request trade/cover
                                </button>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="mt-2 text-xs text-gray-400">Off</p>
                        )}
                        {/* Show open shifts for this day */}
                        {dayOpenShifts.map((s, i) => {
                          const hasClaimed = s.claims?.some(c => c.employee_id === employee?.id);
                          return (
                            <div key={`open-${i}`} className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-xs">
                              <p className="font-medium text-green-700">OPEN SHIFT</p>
                              <p className="text-green-600">{s.start_time} - {s.end_time}</p>
                              {s.station && <p className="text-green-500">{s.station}</p>}
                              {hasClaimed ? (
                                <Badge className="mt-1 bg-yellow-100 text-yellow-700 text-xs">Pending</Badge>
                              ) : (
                                <Button 
                                  size="sm" 
                                  className="mt-1 h-6 text-xs bg-green-600 hover:bg-green-700"
                                  onClick={() => handleClaimShift(s.id)}
                                  disabled={loading}
                                >
                                  Claim
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Hours Tab */}
          <TabsContent value="hours">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>My Time Entries</CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium min-w-[200px] text-center">
                    {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
                  </span>
                  <Button variant="outline" size="icon" onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {timeEntries.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Clock In</TableHead>
                        <TableHead>Clock Out</TableHead>
                        <TableHead>Break</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {timeEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">
                            {format(parseISO(entry.date), 'EEE, MMM d')}
                          </TableCell>
                          <TableCell>
                            {entry.clock_in ? portalFormatTime(entry.clock_in, employee?.time_format) : '-'}
                          </TableCell>
                          <TableCell>
                            {entry.clock_out ? portalFormatTime(entry.clock_out, employee?.time_format) : (
                              <span className="text-purple-600">Working</span>
                            )}
                          </TableCell>
                          <TableCell>{entry.break_minutes || 0} min</TableCell>
                          <TableCell className="text-right font-medium">
                            {(entry.hours_worked || 0).toFixed(2)}h
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={
                              entry.status === 'approved' ? 'bg-green-100 text-green-700' :
                              entry.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100'
                            }>
                              {entry.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No time entries for this week</p>
                  </div>
                )}
                
                {timeEntries.length > 0 && (
                  <div className="mt-4 pt-4 border-t flex justify-between">
                    <span className="text-gray-600">Total Hours This Week:</span>
                    <span className="font-bold text-purple-600">
                      {totalHoursThisWeek.toFixed(2)} hours
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Team Messages</CardTitle>
                <div className="flex items-center gap-2">
                  {messages.filter(m => !m.read_by?.includes(employee?.id)).length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearMessageNotifications}
                      data-testid="clear-message-notifications-btn"
                    >
                      <CheckCheck className="w-4 h-4 mr-2" />
                      Mark All Read
                    </Button>
                  )}
                  <Button 
                    onClick={() => setShowNewMessageModal(true)} 
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="employee-new-message-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    New Message
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {messages.length > 0 ? (
                  <div className="space-y-4">
                    {messages.map((msg) => {
                      const isUnread = !msg.read_by?.includes(employee?.id);
                      const replies = messages.filter(m => m.reply_to === msg.id);
                      const isReply = msg.reply_to;
                      
                      // Skip replies in the main list (they'll be shown under their parent)
                      if (isReply) return null;
                      
                      return (
                        <div
                          key={msg.id}
                          className={`p-4 rounded-lg border ${isUnread ? 'bg-purple-50 border-purple-200' : 'bg-white border-gray-200'}`}
                          data-testid={`message-card-${msg.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="bg-gray-200 text-xs">
                                  {msg.sender_name?.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium text-gray-900">{msg.sender_name}</p>
                                <p className="text-xs text-gray-500">
                                  {msg.created_at && `${format(parseISO(msg.created_at), 'MMM d')}, ${portalFormatTime(msg.created_at, employee?.time_format)}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {msg.is_urgent && (
                                <Badge className="bg-red-100 text-red-700">Urgent</Badge>
                              )}
                              <Badge variant="outline">{msg.type}</Badge>
                              {isUnread && (
                                <button
                                  onClick={() => handleMarkMessageRead(msg.id)}
                                  className="p-1 rounded hover:bg-purple-100 transition-colors"
                                  title="Mark as read"
                                  data-testid={`mark-read-${msg.id}`}
                                >
                                  <CheckCheck className="w-4 h-4 text-purple-600" />
                                </button>
                              )}
                            </div>
                          </div>
                          {msg.subject && (
                            <p className="mt-2 font-medium text-gray-900">{msg.subject}</p>
                          )}
                          <p className="mt-1 text-gray-600 whitespace-pre-wrap">{msg.content}</p>
                          <MessageAttachments attachments={msg.attachments} />
                          
                          {/* Replies */}
                          {replies.length > 0 && (
                            <div className="mt-3 pl-4 border-l-2 border-purple-200 space-y-2">
                              {replies.map(reply => (
                                <div key={reply.id} className="p-2 bg-gray-50 rounded">
                                  <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span className="font-medium text-gray-700">{reply.sender_name}</span>
                                    <span>{`${format(parseISO(reply.created_at), 'MMM d')}, ${portalFormatTime(reply.created_at, employee?.time_format)}`}</span>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{reply.content}</p>
                                  <MessageAttachments attachments={reply.attachments} />
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {/* Reply Input */}
                          <div className="mt-3 pt-3 border-t">
                            <div className="flex gap-2">
                              <Input
                                placeholder="Type a reply..."
                                value={replyTexts[msg.id] || ''}
                                onChange={(e) => setReplyTexts(prev => ({...prev, [msg.id]: e.target.value}))}
                                onKeyPress={(e) => e.key === 'Enter' && handleSendReply(msg)}
                                className="flex-1"
                                data-testid={`reply-input-${msg.id}`}
                              />
                              <Button 
                                size="sm" 
                                onClick={() => handleSendReply(msg)}
                                disabled={!replyTexts[msg.id]?.trim()}
                                className="bg-purple-600 hover:bg-purple-700"
                                data-testid={`reply-btn-${msg.id}`}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No messages</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time Off Tab */}
          <TabsContent value="timeoff">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Time Off Requests</CardTitle>
                <Button 
                  onClick={() => setShowTimeOffModal(true)} 
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="request-time-off-btn"
                >
                  Request Time Off
                </Button>
              </CardHeader>
              <CardContent>
                {timeOffRequests.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {timeOffRequests.map((req) => (
                        <TableRow key={req.id}>
                          <TableCell className="capitalize">{req.type}</TableCell>
                          <TableCell>{format(parseISO(req.start_date), 'MMM d, yyyy')}</TableCell>
                          <TableCell>{format(parseISO(req.end_date), 'MMM d, yyyy')}</TableCell>
                          <TableCell className="max-w-[200px] truncate">{req.reason || '-'}</TableCell>
                          <TableCell>
                            <Badge className={
                              req.status === 'approved' ? 'bg-green-100 text-green-700' :
                              req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              req.status === 'denied' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100'
                            }>
                              {req.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No time off requests</p>
                    <p className="text-sm mt-1">Click &quot;Request Time Off&quot; to submit a new request</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Time Display Format</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose how times are shown across the portal (schedules, hours, messages).
                  </p>
                  <Select
                    value={employee?.time_format || '12h'}
                    onValueChange={async (v) => {
                      try {
                        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/employees/me/preferences`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${secureStorage.getItem('employee_portal_token')}`,
                          },
                          body: JSON.stringify({ time_format: v }),
                        });
                        if (!res.ok) throw new Error('Failed');
                        setEmployee({ ...employee, time_format: v });
                      } catch (err) {
                        alert('Failed to save preference');
                      }
                    }}
                  >
                    <SelectTrigger className="max-w-xs" data-testid="employee-time-format-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="12h">Standard (2:00 PM)</SelectItem>
                      <SelectItem value="24h">Military (14:00)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Availability Tab */}
          <TabsContent value="availability">
            <Card className="border-0 shadow-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>My Availability</CardTitle>
                  <p className="text-xs text-gray-500 mt-1">
                    Tell your manager which days and hours you&apos;re available to be scheduled.
                  </p>
                </div>
                <Button
                  onClick={async () => {
                    try {
                      const saved = await availabilityApi.updateMine(availability);
                      setAvailability(saved);
                      alert('Availability saved!');
                    } catch (err) {
                      alert('Failed to save: ' + err.message);
                    }
                  }}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="save-availability-btn"
                  disabled={!availability}
                >
                  Save
                </Button>
              </CardHeader>
              <CardContent>
                {availability ? (
                  <AvailabilityEditor value={availability} onChange={setAvailability} />
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" />
                    Loading availability...
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

      {/* Shift Trade / Cover Request Modal */}
      <Dialog open={!!shiftRequestModal} onOpenChange={(o) => { if (!o) setShiftRequestModal(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Shift Trade or Cover</DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {shiftRequestModal?.schedule && (
              <div className="p-3 rounded-lg bg-purple-50 border border-purple-200 text-sm">
                <div className="text-xs text-gray-500">Your shift</div>
                <div className="font-medium text-purple-900">
                  {shiftRequestModal.schedule.date} · {portalFormatTime(`2000-01-01T${shiftRequestModal.schedule.start_time}:00`, employee?.time_format)} - {portalFormatTime(`2000-01-01T${shiftRequestModal.schedule.end_time}:00`, employee?.time_format)}
                </div>
              </div>
            )}
            <div>
              <Label>Request type</Label>
              <Select value={shiftRequestForm.type} onValueChange={(v) => setShiftRequestForm({ ...shiftRequestForm, type: v })}>
                <SelectTrigger data-testid="shift-request-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cover">Cover (someone takes my shift)</SelectItem>
                  <SelectItem value="trade">Trade (swap with another shift)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {shiftRequestForm.type === 'trade' && (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                For trade requests, your manager will pair you with another scheduled employee at approval time.
                Provide the teammate&apos;s name in the reason below.
              </div>
            )}
            <div>
              <Label>Reason (optional)</Label>
              <Textarea
                rows={3}
                value={shiftRequestForm.reason}
                onChange={(e) => setShiftRequestForm({ ...shiftRequestForm, reason: e.target.value })}
                placeholder={shiftRequestForm.type === 'trade'
                  ? 'e.g., Swap with Maria — she said yes'
                  : 'e.g., Family event, need someone to cover'}
                data-testid="shift-request-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShiftRequestModal(null)}>Cancel</Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700"
              onClick={async () => {
                try {
                  await shiftRequestsApi.create({
                    type: shiftRequestForm.type,
                    requester_schedule_id: shiftRequestModal.schedule.id,
                    target_employee_id: shiftRequestForm.target_employee_id || null,
                    target_schedule_id: shiftRequestForm.target_schedule_id || null,
                    reason: shiftRequestForm.reason,
                  });
                  alert('Request submitted. A manager will review it.');
                  setShiftRequestModal(null);
                  setShiftRequestForm({ type: 'cover', target_schedule_id: '', target_employee_id: '', reason: '' });
                  const sr = await shiftRequestsApi.list({ employeeId: employee?.id });
                  setMyShiftRequests(sr || []);
                } catch (e) {
                  alert('Failed to submit: ' + e.message);
                }
              }}
              disabled={shiftRequestForm.type === 'trade' && !shiftRequestForm.reason}
              data-testid="submit-shift-request"
            >
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        {/* Time Off Request Modal */}
        <Dialog open={showTimeOffModal} onOpenChange={(o) => { setShowTimeOffModal(o); if (!o) setBlackoutHit(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Request Time Off</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {upcomingBlackouts.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs" data-testid="employee-blackout-banner">
                  <p className="font-semibold text-red-700 mb-1 flex items-center gap-1">
                    <Ban className="w-3 h-3" /> Blackout dates — unavailable for time-off requests
                  </p>
                  <ul className="text-red-700 space-y-0.5 ml-4 list-disc">
                    {upcomingBlackouts.map((b) => (
                      <li key={b.id}>
                        <strong>{b.label || 'Blackout'}:</strong> {b.start_date}
                        {b.end_date && b.end_date !== b.start_date ? ` – ${b.end_date}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal ${!newTimeOff.start_date ? 'text-muted-foreground' : ''}`}
                        data-testid="timeoff-start-date"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {newTimeOff.start_date || 'Pick a start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <ShadCalendar
                        mode="single"
                        selected={newTimeOff.start_date ? new Date(`${newTimeOff.start_date}T00:00:00`) : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          const ds = format(d, 'yyyy-MM-dd');
                          setBlackoutHit(null);
                          setNewTimeOff({ ...newTimeOff, start_date: ds, end_date: newTimeOff.end_date && newTimeOff.end_date < ds ? ds : newTimeOff.end_date });
                        }}
                        disabled={(d) => {
                          const ds = format(d, 'yyyy-MM-dd');
                          return ds < format(new Date(), 'yyyy-MM-dd') || isDateInBlackout(ds);
                        }}
                        modifiers={blackoutModifiers}
                        modifiersClassNames={BLACKOUT_MODIFIER_CLASSES}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={`w-full justify-start text-left font-normal ${!newTimeOff.end_date ? 'text-muted-foreground' : ''}`}
                        data-testid="timeoff-end-date"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {newTimeOff.end_date || 'Pick an end date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <ShadCalendar
                        mode="single"
                        selected={newTimeOff.end_date ? new Date(`${newTimeOff.end_date}T00:00:00`) : undefined}
                        onSelect={(d) => {
                          if (!d) return;
                          const ds = format(d, 'yyyy-MM-dd');
                          setBlackoutHit(null);
                          setNewTimeOff({ ...newTimeOff, end_date: ds });
                        }}
                        disabled={(d) => {
                          const ds = format(d, 'yyyy-MM-dd');
                          const minDate = newTimeOff.start_date || format(new Date(), 'yyyy-MM-dd');
                          return ds < minDate || isDateInBlackout(ds);
                        }}
                        modifiers={blackoutModifiers}
                        modifiersClassNames={BLACKOUT_MODIFIER_CLASSES}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              {blackoutHit && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2" data-testid="blackout-error-banner">
                  <Ban className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-red-700 text-sm">
                    <strong>{blackoutHit.date}</strong> is a blackout date ({blackoutHit.label}). Time off cannot be requested for this date.
                  </p>
                </div>
              )}
              <div>
                <Label>Type</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white"
                  value={newTimeOff.type}
                  onChange={(e) => setNewTimeOff({...newTimeOff, type: e.target.value})}
                  data-testid="timeoff-type"
                >
                  <option value="vacation">Vacation</option>
                  <option value="sick">Sick Leave</option>
                  <option value="personal">Personal Day</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <Label>Reason (Optional)</Label>
                <Input
                  placeholder="Brief description..."
                  value={newTimeOff.reason}
                  onChange={(e) => setNewTimeOff({...newTimeOff, reason: e.target.value})}
                  data-testid="timeoff-reason"
                />
              </div>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTimeOffModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  const hit = checkRangeForBlackout(newTimeOff.start_date, newTimeOff.end_date);
                  if (hit && hit.block) {
                    setBlackoutHit(hit);
                    return;
                  }
                  setBlackoutHit(null);
                  handleSubmitTimeOff();
                }}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={loading || !newTimeOff.start_date || !newTimeOff.end_date}
                data-testid="submit-timeoff-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Message Modal */}
        <Dialog open={showNewMessageModal} onOpenChange={setShowNewMessageModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send a Message</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Send To <span className="text-red-500">*</span></Label>
                <Select 
                  value={newMessage.recipient_id} 
                  onValueChange={(value) => {
                    const recipient = recipients.find(r => r.id === value);
                    setNewMessage({
                      ...newMessage, 
                      recipient_id: value, 
                      recipient_name: recipient?.name || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="employee-message-recipient">
                    <SelectValue placeholder="Select recipient..." />
                  </SelectTrigger>
                  <SelectContent>
                    {groupRecipients.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50">Group Chats</div>
                        {groupRecipients.map(r => (
                          <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                        ))}
                      </>
                    )}
                    {individualRecipients.length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-gray-500 bg-gray-50 mt-2">Managers</div>
                        {individualRecipients.map(r => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name} {r.role && <span className="text-gray-500">({r.role})</span>}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input
                  placeholder="Message subject..."
                  value={newMessage.subject}
                  onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                  data-testid="employee-message-subject"
                />
              </div>
              <div>
                <Label>Message <span className="text-red-500">*</span></Label>
                <Textarea
                  placeholder="Type your message..."
                  value={newMessage.content}
                  onChange={(e) => setNewMessage({...newMessage, content: e.target.value})}
                  rows={4}
                  data-testid="employee-message-content"
                />
              </div>
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNewMessageModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSendNewMessage} 
                className="bg-purple-600 hover:bg-purple-700"
                disabled={loading || !newMessage.content || !newMessage.recipient_id}
                data-testid="employee-send-message-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Send Message
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default EmployeePortal;
